import React, { useState, useEffect, useMemo } from 'react';
import { Message, Table, Loader, Button, Icon, Header } from 'semantic-ui-react';
import axios from 'axios';
import RequestDetailsModal from './RequestDetailsModal';
import FilterPanel from './FilterPanel';
import PaginationFooter from './PaginationFooter';
import { useTablePreferences } from '../hooks/useTablePreferences';
import { useRequestFilters } from '../hooks/useRequestFilters';
import { formatStatus, getStatusColor, getCloudName, getSubmittedBy, isRequestForm3, highlightSearchTerms, getBatchBadges } from '../utils/requestHelpers';
import { formatTimestamp } from '../utils/timezone.utils';
import { useTimezone } from '../hooks/useTimezone';

const ApprovalHistory = ({
    userEmail,
    userRoles,
    approverCategories,
    approverCategoriesSingleTenancy,
    approverCategoriesMultiTenancy
}) => {
    const userTimezone = useTimezone(); // Listen to timezone changes for re-renders
    const [approvalHistory, setApprovalHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [showFilters, setShowFilters] = useState(false);

    // Sorting state for approval history table
    const [sortColumn, setSortColumn] = useState('timestamp');
    const [sortDirection, setSortDirection] = useState('descending'); // 'ascending' | 'descending'

    // Use reusable hooks
    const { rowsPerPage, tableDensity, activePage, setActivePage } = useTablePreferences();
    const {
        // Basic filters
        statusFilter,
        cloudNameFilter,
        categoryFilter,
        requestIdFilter,
        // Advanced filters
        globalSearch,
        selectedStatuses,
        selectedCategories,
        dateRangeStart,
        dateRangeEnd,
        submittedByFilter,
        // Saved filters
        savedFilters,
        filterPresetName,
        availableStatuses,
        availableCategories,
        // Setters
        setStatusFilter,
        setCloudNameFilter,
        setCategoryFilter,
        setRequestIdFilter,
        setGlobalSearch,
        setSelectedStatuses,
        setSelectedCategories,
        setDateRangeStart,
        setDateRangeEnd,
        setSubmittedByFilter,
        setFilterPresetName,
        // Actions
        hasActiveFilters,
        filteredRequests: filteredHistory,
        resetFilters,
        saveCurrentFilters,
        loadFilterPreset,
        deleteFilterPreset
    } = useRequestFilters(approvalHistory, 'approval-history');

    // Calculate active filter count for badge
    const activeFilterCount = [
        statusFilter,
        cloudNameFilter,
        categoryFilter,
        requestIdFilter,
        globalSearch,
        selectedStatuses.length,
        selectedCategories.length,
        dateRangeStart,
        dateRangeEnd,
        submittedByFilter
    ].filter(v => v && v !== 0).length;

    // Reset page when filters change
    const handleFilterChange = () => {
        setActivePage(1);
    };

    const handleResetFilters = () => {
        resetFilters();
        setActivePage(1);
    };

    const fetchApprovalHistory = async () => {
        try {
            setLoading(true);
            const response = await axios.get('https://qa-tenantbridge.parablu.com/api/requests', {
                withCredentials: true
            });

            const validRequests = response.data.requests.filter(request => {
                if (!request || !request.id || !request.formData) {
                    console.warn('Skipping invalid request in approval history:', request);
                    return false;
                }

                return true;
            });

            const filteredHistory = validRequests.filter(request => {
                // Only requestForm3 indicates a true Multi Tenancy Form request
                const isMultiTenancy = !!(request.formData?.requestForm3);

                // Filter by approver's assigned categories (ST or MT based on request type)
                const relevantApproverCategories = isMultiTenancy
                    ? approverCategoriesMultiTenancy
                    : approverCategoriesSingleTenancy;

                // Fallback to old approverCategories if new fields are empty (backward compatibility)
                const categoriesToCheck = (relevantApproverCategories && relevantApproverCategories.length > 0)
                    ? relevantApproverCategories
                    : (approverCategories || []);

                if (categoriesToCheck.length > 0 && request.category) {
                    const requestCategory = request.category.trim().toUpperCase();
                    const hasMatchingCategory = categoriesToCheck.some(cat =>
                        cat && cat.trim().toUpperCase() === requestCategory
                    );
                    // Show all requests matching approver's categories (any status)
                    return hasMatchingCategory;
                }
                // If no categories assigned, don't show any requests (safety measure)
                return false;
            });

            setApprovalHistory(filteredHistory);
            setError(null);
        } catch (err) {
            console.error('Error fetching approval history:', err);
            setError('Failed to load approval history: ' + (err.response?.data?.message || err.message));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (userEmail) {
            fetchApprovalHistory();
        }
    }, [userEmail, approverCategories, approverCategoriesSingleTenancy, approverCategoriesMultiTenancy]);

    const getApprovedBy = (request) => {
        return request.approver || '';
    };

    const getActionTaken = (request, userEmail) => {
        const approver = String(request.approver || '').trim();
        const normalizedUserEmail = String(userEmail || '').trim().toLowerCase();
        const normalizedApprover = approver.toLowerCase();
        const normalizedStatus = String(request.status || '').trim().toUpperCase();

        const approverStatuses = String(request.approverStatus || '')
            .split(',')
            .map(status => status.trim().toUpperCase())
            .filter(Boolean);

        const isApprover = normalizedApprover && normalizedApprover === normalizedUserEmail;
        const hasApproved = approverStatuses.includes('APPROVED');
        const hasRejected = approverStatuses.includes('REJECTED');
        const hasCancelled = approverStatuses.includes('CANCELLED');

        if (normalizedStatus === 'REJECTED' || hasRejected) {
            return 'Rejected';
        }

        if (normalizedStatus === 'WITHDRAWN') {
            if (hasApproved) {
                if (request.approverComment && request.approverComment.includes('withdrawn by')) {
                    const withdrawalUser = request.approverComment.match(/withdrawn by ([^.]+)/)?.[1] || 'user';
                    return `Approved (then withdrawn by ${withdrawalUser})`;
                }
                return 'Approved (then withdrawn by user)';
            }
            return hasCancelled ? 'Cancelled' : 'Withdrawn';
        }

        if (hasCancelled || normalizedStatus.includes('CANCELLED')) {
            return 'Cancelled';
        }

        if (hasApproved) {
            return 'Approved';
        }

        if (isApprover) {
            return 'Approved';
        }

        // If request is visible in approver history but we don't have explicit approver status,
        // infer action from the final request status to avoid confusing "Unknown" labels.
        if (normalizedStatus.includes('READY') || normalizedStatus.includes('COMPLETED') || normalizedStatus.includes('CREATED')) {
            return 'Approved';
        }

        if (normalizedStatus.includes('PENDING')) {
            return 'Pending';
        }

        return normalizedStatus
            ? normalizedStatus.charAt(0) + normalizedStatus.slice(1).toLowerCase().replace(/_/g, ' ')
            : 'Unknown';
    };

    const getComments = (request, userEmail) => {
        const approver = request.approver || '';

        const isApprover = approver.toLowerCase() === userEmail.toLowerCase();

        if (request.status === 'REJECTED') {
            if (isApprover && request.approverComment) {
                return request.approverComment;
            }
        } else if (request.status === 'WITHDRAWN') {
            // For withdrawn requests, show the withdrawal reason/comment
            if (request.approverComment) {
                return request.approverComment;
            }
            // If no comment, show a default message
            return 'Request was withdrawn';
        }
        return '';
    };

    const handleSort = (column) => {
        if (sortColumn === column) {
            setSortDirection(sortDirection === 'ascending' ? 'descending' : 'ascending');
        } else {
            setSortColumn(column);
            // Default to descending for timestamp, ascending otherwise
            setSortDirection(column === 'timestamp' ? 'descending' : 'ascending');
        }
    };

    // Calculate sorted and paginated requests
    const sortedHistory = useMemo(() => {
        const items = filteredHistory.slice();
        const directionFactor = sortDirection === 'descending' ? -1 : 1;

        const getFieldValue = (req, column) => {
            switch (column) {
                case 'timestamp':
                    return new Date(req.timestamp).getTime() || 0;
                case 'requestId':
                    return String(req.id || '').toLowerCase();
                case 'submittedBy':
                    return getSubmittedBy(req).toLowerCase();
                case 'cloudName':
                    return getCloudName(req).toLowerCase();
                case 'category':
                    return String(req.category || '').toLowerCase();
                case 'approvedBy':
                    return getApprovedBy(req).toLowerCase();
                case 'action':
                    return getActionTaken(req, userEmail).toLowerCase();
                case 'comments':
                    return getComments(req, userEmail).toLowerCase();
                default:
                    return '';
            }
        };

        items.sort((a, b) => {
            const aVal = getFieldValue(a, sortColumn);
            const bVal = getFieldValue(b, sortColumn);

            if (aVal < bVal) return -1 * directionFactor;
            if (aVal > bVal) return 1 * directionFactor;
            return 0;
        });

        return items;
    }, [filteredHistory, sortColumn, sortDirection, userEmail]);

    const totalPages = Math.max(1, Math.ceil(sortedHistory.length / Math.max(1, rowsPerPage)));
    const pageStart = (activePage - 1) * rowsPerPage;
    const pageEnd = pageStart + rowsPerPage;
    const visibleRequests = sortedHistory.slice(pageStart, pageEnd);


    if (loading) return (
        <div style={{ padding: '2rem', textAlign: 'center' }}>
            <Loader active inline='centered'>Loading approval history...</Loader>
        </div>
    );

    if (error) return <Message negative>{error}</Message>;

    return (
        <div style={{
            marginTop: '12px',
            display: 'flex',
            flexDirection: 'column',
            height: 'calc(100vh - 180px)',
            minHeight: '500px',
            position: 'relative',
            overflow: 'hidden'
        }}>
            {/* Fixed Header */}
            <div style={{
                flexShrink: 0,
                backgroundColor: 'white',
                zIndex: 100,
                paddingBottom: '12px',
                borderBottom: '1px solid rgba(0,0,0,0.08)',
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            fontSize: '1.35rem',
                            fontWeight: 700,
                            color: '#062D4D'
                        }}
                    >
                        <Icon name="check circle" color="blue" />
                        <span>Approval History</span>
                    </div>
                    <Button
                        icon
                        labelPosition="left"
                        onClick={() => setShowFilters(!showFilters)}
                        style={{ marginLeft: 'auto' }}
                        color={showFilters ? 'blue' : undefined}
                        primary={hasActiveFilters && !showFilters}
                    >
                        <Icon name="filter" />
                        {showFilters ? 'Hide Filters' : 'Show Filters'}
                        {hasActiveFilters && !showFilters && (
                            <span style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginLeft: '8px',
                                backgroundColor: '#fff',
                                color: '#2185d0',
                                fontSize: '11px',
                                fontWeight: 700,
                                minWidth: '22px',
                                height: '22px',
                                borderRadius: '12px',
                                padding: '0 7px',
                                lineHeight: 1,
                                letterSpacing: '0.3px',
                            }}>
                                {activeFilterCount}
                            </span>
                        )}
                    </Button>
                </div>
            </div>

            {/* Sidebar Filter Panel */}
            {showFilters && (
                <FilterPanel
                        mode="advanced"
                        onClose={() => setShowFilters(false)}
                        // Basic filters
                        statusFilter={statusFilter}
                        cloudNameFilter={cloudNameFilter}
                        categoryFilter={categoryFilter}
                        requestIdFilter={requestIdFilter}
                        // Advanced filters
                        globalSearch={globalSearch}
                        selectedStatuses={selectedStatuses}
                        selectedCategories={selectedCategories}
                        dateRangeStart={dateRangeStart}
                        dateRangeEnd={dateRangeEnd}
                        submittedByFilter={submittedByFilter}
                        // Saved filters
                        savedFilters={savedFilters}
                        filterPresetName={filterPresetName}
                        availableStatuses={availableStatuses}
                        availableCategories={availableCategories}
                        // Basic callbacks
                        onStatusFilterChange={setStatusFilter}
                        onCloudNameFilterChange={setCloudNameFilter}
                        onCategoryFilterChange={setCategoryFilter}
                        onRequestIdFilterChange={setRequestIdFilter}
                        // Advanced callbacks
                        onGlobalSearchChange={setGlobalSearch}
                        onSelectedStatusesChange={setSelectedStatuses}
                        onSelectedCategoriesChange={setSelectedCategories}
                        onDateRangeStartChange={setDateRangeStart}
                        onDateRangeEndChange={setDateRangeEnd}
                        onSubmittedByFilterChange={setSubmittedByFilter}
                        // Saved filters callbacks
                        onFilterPresetNameChange={setFilterPresetName}
                        onSaveFilterPreset={saveCurrentFilters}
                        onLoadFilterPreset={loadFilterPreset}
                        onDeleteFilterPreset={deleteFilterPreset}
                        // Actions
                        onResetFilters={handleResetFilters}
                        hasActiveFilters={hasActiveFilters}
                        onFilterChange={handleFilterChange}
                    />
            )}

            {/* Scrollable Table Container */}
            <div style={{
                flex: 1,
                overflowY: 'auto',
                overflowX: 'auto',
                minHeight: 0,
                marginTop: showFilters ? '0' : '12px',
                position: 'relative',
                zIndex: 1
            }}>
                {filteredHistory.length === 0 ? (
                    <Message info>
                        <Message.Header>No approval history found</Message.Header>
                        <p>{hasActiveFilters ? 'No records match the selected filters. Try adjusting your filters.' : "You haven't approved or rejected any requests yet."}</p>
                    </Message>
                ) : (
                    <>
                        <Table celled unstackable responsive sortable compact={tableDensity === 'compact'} size={tableDensity === 'compact' ? 'small' : undefined} style={{ minWidth: '900px' }}>
                            <Table.Header style={{ position: 'sticky', top: 0, zIndex: 50, backgroundColor: 'white', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                                <Table.Row>
                                    <Table.HeaderCell
                                        sorted={sortColumn === 'timestamp' ? sortDirection : null}
                                        onClick={() => handleSort('timestamp')}
                                        style={{ backgroundColor: 'white', cursor: 'pointer' }}
                                    >
                                        Timestamp
                                    </Table.HeaderCell>
                                    <Table.HeaderCell
                                        sorted={sortColumn === 'requestId' ? sortDirection : null}
                                        onClick={() => handleSort('requestId')}
                                        style={{ backgroundColor: 'white', cursor: 'pointer' }}
                                    >
                                        Request ID
                                    </Table.HeaderCell>
                                    <Table.HeaderCell
                                        sorted={sortColumn === 'submittedBy' ? sortDirection : null}
                                        onClick={() => handleSort('submittedBy')}
                                        style={{ backgroundColor: 'white', cursor: 'pointer' }}
                                    >
                                        Submitted By
                                    </Table.HeaderCell>
                                    <Table.HeaderCell
                                        sorted={sortColumn === 'cloudName' ? sortDirection : null}
                                        onClick={() => handleSort('cloudName')}
                                        style={{ backgroundColor: 'white', cursor: 'pointer' }}
                                    >
                                        Cloud Name
                                    </Table.HeaderCell>
                                    <Table.HeaderCell
                                        sorted={sortColumn === 'category' ? sortDirection : null}
                                        onClick={() => handleSort('category')}
                                        style={{ backgroundColor: 'white', cursor: 'pointer' }}
                                    >
                                        Category
                                    </Table.HeaderCell>
                                    <Table.HeaderCell
                                        sorted={sortColumn === 'approvedBy' ? sortDirection : null}
                                        onClick={() => handleSort('approvedBy')}
                                        style={{ backgroundColor: 'white', cursor: 'pointer' }}
                                    >
                                        Approved By
                                    </Table.HeaderCell>
                                    <Table.HeaderCell
                                        sorted={sortColumn === 'action' ? sortDirection : null}
                                        onClick={() => handleSort('action')}
                                        style={{ backgroundColor: 'white', cursor: 'pointer' }}
                                    >
                                        Action Taken
                                    </Table.HeaderCell>
                                    <Table.HeaderCell
                                        sorted={sortColumn === 'comments' ? sortDirection : null}
                                        onClick={() => handleSort('comments')}
                                        style={{ backgroundColor: 'white', cursor: 'pointer' }}
                                    >
                                        Comments
                                    </Table.HeaderCell>
                                    <Table.HeaderCell style={{ backgroundColor: 'white' }}>Actions</Table.HeaderCell>
                                </Table.Row>
                            </Table.Header>

                            <Table.Body>
                                {visibleRequests.map(request => {
                                    const approvedBy = getApprovedBy(request);
                                    const actionTaken = getActionTaken(request, userEmail);
                                    const comments = getComments(request, userEmail);
                                    
                                    // Collect all active search terms for highlighting
                                    const searchTerms = [
                                        globalSearch,
                                        requestIdFilter,
                                        cloudNameFilter,
                                        categoryFilter,
                                        statusFilter,
                                        submittedByFilter
                                    ].filter(term => term && term.trim());

                                    return (
                                        <Table.Row key={request.id}>
                                            <Table.Cell>
                                                {highlightSearchTerms(formatTimestamp(request.timestamp), searchTerms)}
                                            </Table.Cell>
                                            <Table.Cell>
                                                {highlightSearchTerms(request.id, searchTerms)}
                                            </Table.Cell>
                                            <Table.Cell>
                                                {highlightSearchTerms(getSubmittedBy(request), searchTerms)}
                                            </Table.Cell>
                                            <Table.Cell>
                                                {highlightSearchTerms(getCloudName(request), searchTerms)}
                                                {getBatchBadges(request).map((badge, idx) => (
                                                    <span
                                                        key={idx}
                                                        style={{
                                                            marginLeft: idx === 0 ? '8px' : '4px',
                                                            padding: '2px 6px',
                                                            backgroundColor: '#e1f5fe',
                                                            color: '#35B7F4',
                                                            borderRadius: '3px',
                                                            fontSize: '0.8em',
                                                            fontWeight: 'bold'
                                                        }}
                                                    >
                                                        {badge}
                                                    </span>
                                                ))}
                                            </Table.Cell>
                                            <Table.Cell>
                                                {highlightSearchTerms(
                                                    request.category === 'PROD' &&
                                                    request.formData?.requestForm3
                                                        ? 'PROD / POC'
                                                        : (request.category || 'Unknown'),
                                                    searchTerms
                                                )}
                                            </Table.Cell>
                                            <Table.Cell>
                                                {highlightSearchTerms(approvedBy || '-', searchTerms)}
                                            </Table.Cell>
                                            <Table.Cell className={getStatusColor(request.status)}>
                                                {highlightSearchTerms(`${actionTaken} - (${request.status || 'Unknown'})`, searchTerms)}
                                            </Table.Cell>
                                            <Table.Cell>
                                                {highlightSearchTerms(comments || '-', searchTerms)}
                                            </Table.Cell>
                                            <Table.Cell>
                                                <Button
                                                    size="small"
                                                    content="View Details"
                                                    onClick={() => {
                                                        setSelectedRequest(request);
                                                        setShowDetailsModal(true);
                                                    }}
                                                />
                                            </Table.Cell>
                                        </Table.Row>
                                    );
                                })}
                            </Table.Body>
                        </Table>
                    </>
                )}
            </div>

            {/* Fixed Pagination */}
            {filteredHistory.length > 0 && (
                <PaginationFooter
                    activePage={activePage}
                    totalPages={totalPages}
                    totalItems={approvalHistory.length}
                    filteredItems={sortedHistory}
                    hasActiveFilters={hasActiveFilters}
                    onPageChange={setActivePage}
                    pageStart={pageStart}
                    pageEnd={pageEnd}
                />
            )}

            <RequestDetailsModal
                open={showDetailsModal}
                onClose={() => {
                    setShowDetailsModal(false);
                    setSelectedRequest(null);
                }}
                request={selectedRequest}
                showApprovalActions={false}
            />
        </div>
    );
};

export default ApprovalHistory;